import React, { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * MobileCardList — the phone rendering of an AG Grid, derived from the grid's
 * own column definitions.
 *
 * The alternative was a bespoke card layout per screen, and there are around a
 * hundred grids here. This reads `columnDefs` instead, so a screen opts in with
 * one line naming which fields matter:
 *
 *   mobileCard={{ title: 'j_number', subtitle: 'name', meta: ['status', 'eta'] }}
 *
 * Deliberately not used for the heavy report grids. The widest here carry
 * 40–52 columns; rendered as a card that is a wall of text two screens tall,
 * which is worse than scrolling a table. Those stay a grid and stay a desk
 * tool.
 */

const colFor = (columnDefs, field) =>
  columnDefs.find((c) => c.field === field || c.colId === field);

/**
 * Resolves a cell the way the grid would.
 *
 * valueGetter/valueFormatter are AG Grid callbacks and expect the full params
 * object; this can only supply the part of it that is knowable outside a live
 * grid. Anything that reaches for `api`, `node` or `context` will throw, so
 * each call falls back to the raw field rather than taking the card down —
 * a slightly unformatted value beats a blank screen.
 */
const valueOf = (row, field, columnDefs) => {
  const col = colFor(columnDefs, field);
  let value = row?.[field];

  if (typeof col?.valueGetter === 'function') {
    try { value = col.valueGetter({ data: row, colDef: col, getValue: (f) => row?.[f] }); }
    catch (_) { /* keep the raw field */ }
  }
  if (typeof col?.valueFormatter === 'function') {
    try { value = col.valueFormatter({ value, data: row, colDef: col }); }
    catch (_) { /* keep the unformatted value */ }
  }
  return value;
};

const labelOf = (columnDefs, field) => colFor(columnDefs, field)?.headerName || field;

const isBlank = (v) => v === null || v === undefined || v === '';

const MobileCardList = ({
  rowData, columnDefs, config, onRowClick, pageSize, loading,
  noRowsMessage = 'No data',
}) => {
  const [shown, setShown] = useState(pageSize);
  const { title, subtitle, meta = [], renderTitle, renderBadge, renderSubtitle } = config;

  if (!loading && !rowData.length) {
    return (
      <div className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-400">{noRowsMessage}</div>
    );
  }

  const visible = rowData.slice(0, shown);

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {visible.map((row, i) => {
        const clickable = typeof onRowClick === 'function';
        return (
          <div
            key={row?.id ?? i}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onRowClick({ data: row }) : undefined}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick({ data: row }); }
            } : undefined}
            className={[
              'tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-p-3',
              clickable ? 'tw-cursor-pointer active:tw-bg-slate-50' : '',
            ].join(' ')}
          >
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
              <div className="tw-min-w-0">
                <div className="tw-text-sm tw-font-semibold tw-text-slate-900 tw-break-words">
                  {renderTitle ? renderTitle(row) : valueOf(row, title, columnDefs)}
                </div>
                {(renderSubtitle || subtitle) && (
                  <div className="tw-mt-0.5 tw-text-xs tw-text-slate-500 tw-break-words">
                    {renderSubtitle ? renderSubtitle(row) : valueOf(row, subtitle, columnDefs)}
                  </div>
                )}
              </div>
              {renderBadge && <div className="tw-shrink-0">{renderBadge(row)}</div>}
            </div>

            {meta.length > 0 && (
              <dl className="tw-mt-2.5 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-mb-0">
                {meta.map((field) => {
                  const v = valueOf(row, field, columnDefs);
                  return (
                    <div key={field} className="tw-min-w-0">
                      <dt className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-400">
                        {labelOf(columnDefs, field)}
                      </dt>
                      {/* tw-ml-0: Tailwind Preflight is disabled in this app, so
                          <dd> keeps the UA's 40px margin-inline-start and every
                          value sat indented away from its own label. */}
                      <dd className="tw-ml-0 tw-mb-0 tw-text-xs tw-text-slate-700 tw-break-words">
                        {isBlank(v) ? <span className="tw-text-slate-300">—</span> : v}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
          </div>
        );
      })}

      {shown < rowData.length && (
        <button
          type="button"
          onClick={() => setShown((n) => n + pageSize)}
          className="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-600 tw-cursor-pointer hover:tw-bg-slate-50"
        >
          Load more ({rowData.length - shown} left)
        </button>
      )}
    </div>
  );
};

MobileCardList.propTypes = {
  rowData: PropTypes.array.isRequired,
  columnDefs: PropTypes.array.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    meta: PropTypes.arrayOf(PropTypes.string),
    renderTitle: PropTypes.func,
    renderSubtitle: PropTypes.func,
    renderBadge: PropTypes.func,
  }).isRequired,
  onRowClick: PropTypes.func,
  pageSize: PropTypes.number,
  loading: PropTypes.bool,
  noRowsMessage: PropTypes.string,
};

MobileCardList.defaultProps = { pageSize: 20 };

export default MobileCardList;
